import React, { useState, useEffect } from 'react';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CharacterCounter } from '@admin/components/character_counter';
import { client } from '@admin/libraries/apollo';
import { useMessagesWidgetBuildMessageMutation, MessageInput } from '@admin/schema';

import { canUpdateCxInbox } from './permissions';

interface ReplyButtonProps {
  disabled: boolean;
  recipientType: 'Account' | 'Lead' | 'Assignment';
  recipientId: string;
  phoneId: string;
  replyToName?: string;
}

const SMS_CONSTANTS = {
  MAX_LENGTH: 1600,
  PLACEHOLDER: 'What would you like to say?',
};

export const ReplyButton: React.FC<ReplyButtonProps> = ({
  disabled,
  recipientType,
  recipientId,
  phoneId,
  replyToName,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [errorVisible, setErrorVisible] = useState(false);

  const [buildMessageMutation, { loading: sending }] = useMessagesWidgetBuildMessageMutation({ client });

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setMessageBody('');
    setErrorVisible(false);
  };

  const trySending = async () => {
    if (!messageBody.trim()) {
      setErrorVisible(true);
      return;
    }

    const bodyToSend = messageBody.trim();
    setErrorVisible(false);

    try {
      const messageInput: MessageInput = {
        body: bodyToSend,
        recipientType,
        recipientId,
        phoneId,
      };

      await buildMessageMutation({
        variables: { input: messageInput },
      });

      // Success - close modal and refresh the page
      closeModal();
      window.location.reload();
    } catch (error) {
      console.error('Failed to send message:', error);
      setErrorVisible(true);
    }
  };

  const hideErrorMessage = () => {
    setErrorVisible(false);
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageBody(e.target.value);
    hideErrorMessage();
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal]);

  if (!canUpdateCxInbox()) {
    return null;
  }

  return (
    <span className="reply-button-container">
      <button className="btn btn-default" onClick={openModal} disabled={disabled || sending} type="button">
        {sending ? 'Sending...' : 'Reply'}
      </button>
      {showModal && (
        <>
          <div className="modal-backdrop" onClick={closeModal}></div>
          <div className="reply-modal" style={{ display: 'block' }}>
            <div className="mar-btm">
              <span className="text-bold">Reply</span>
              {replyToName && <span> to {replyToName}</span>}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  closeModal();
                }}
              >
                <FontAwesomeIcon icon={faTimes} size="lg" className="pull-right" />
              </a>
            </div>
            <div className="form-group">
              <textarea
                className="form-control"
                name="message-body"
                maxLength={SMS_CONSTANTS.MAX_LENGTH}
                placeholder={SMS_CONSTANTS.PLACEHOLDER}
                value={messageBody}
                onChange={handleTextAreaChange}
                rows={4}
              />
            </div>
            <div className="text-right">
              {errorVisible && <span className="mar-rgt text-danger">Please enter a reply</span>}
              <div className="mar-rgt" style={{ display: 'inline-block' }}>
                <CharacterCounter characterCount={messageBody.length} characterLimit={SMS_CONSTANTS.MAX_LENGTH} />
              </div>
              <button className="btn btn-primary" onClick={trySending} disabled={disabled || sending} type="button">
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </>
      )}
    </span>
  );
};
